/**
 * 조직 자동 전환 결정 로직 (순수 함수).
 *
 * CHG-149: 다중 조직 사용자가 다른 조직의 리소스 링크에 접근했을 때
 * 활성 조직을 자동 전환해야 하는지 판단한다.
 *
 * 본 모듈은 React/스토어 의존이 0 — 단위 테스트 가능하도록 추출.
 * 실제 전환 부수효과(setActiveOrgId, queryClient.invalidateQueries, toast)는
 * `useOrgAutoSwitch` 훅에서 본 함수 결과를 받아 수행한다.
 */

export type AutoSwitchStatus =
  | 'noop'         // targetOrgId가 없음 — 아무것도 안 함
  | 'same'         // 이미 활성 조직 — 전환 불필요
  | 'switched'     // 멤버이며 활성과 다름 — 전환 발생
  | 'not-a-member'; // 사용자가 그 조직의 멤버가 아님 — 권한 안내 필요

export interface AutoSwitchDecision {
  status: AutoSwitchStatus;
  /** 'switched' 또는 'not-a-member' 케이스에서 채워짐 */
  targetOrgId?: string;
}

/**
 * 자동 전환 의사결정.
 *
 * @param targetOrgId 리소스가 속한 조직 ID (null/undefined면 noop)
 * @param activeOrgId 현재 활성 조직 ID
 * @param memberOrgIds 사용자가 멤버인 조직 ID들 (Set으로 O(1) 조회)
 */
export function decideAutoSwitch(
  targetOrgId: string | null | undefined,
  activeOrgId: string | null,
  memberOrgIds: Set<string>,
): AutoSwitchDecision {
  if (!targetOrgId) return { status: 'noop' };
  if (targetOrgId === activeOrgId) return { status: 'same' };
  if (!memberOrgIds.has(targetOrgId)) return { status: 'not-a-member', targetOrgId };
  return { status: 'switched', targetOrgId };
}
